'use client';
import React, { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import { Action, Resource, Permission } from '@/types/Policy';
import { User } from '@/types/User';
import { can, hasPermission, hasAllPermissions, getAccessibleResources } from '@/lib/policy';
import instance from '@/lib/axiosInstance';


interface PolicyContextType {
  can: (action: Action, resource: Resource) => boolean;
  hasPermission: (permission: string) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  applyPolicy: (policy: (user: User | null) => boolean) => boolean;
  hasPermissionToAccess: () => boolean;
  accessibleResources: Resource[];
  permissions: Permission[];
  isLoading: boolean;
}

const PolicyContext = createContext<PolicyContextType | undefined>(undefined);

export function PolicyProvider({
  children,
  user
}: {
  children: ReactNode;
  user: User | null;
}) {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [accessibleResources, setAccessibleResources] = useState<Resource[]>([]);

  // Fetch all available permissions from the API
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const response = await instance.get('/permissions');

        if (response.status === 200) {
          const data = await response.data;
          setPermissions(data.data);
        }
      } catch (error) {
        console.error('Failed to fetch permissions:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPermissions();
  }, []);

  // Update accessible resources when user changes
  useEffect(() => {
    if (user) {
      setAccessibleResources(getAccessibleResources(user));
    } else {
      setAccessibleResources([]);
    }
  }, [user]);

  const value = {
    can: (action: Action, resource: Resource) => can(user, action, resource),
    hasPermission: (permission: string) => hasPermission(user, permission),
    hasAllPermissions: (permissions: string[]) => hasAllPermissions(user, permissions),
    applyPolicy: (policy: (user: User | null) => boolean) => policy(user),
    hasPermissionToAccess: () => {
      if (!user || !user.roles.length) {
        return false;
      }

      if (user.roles.some(role => role.name === 'admin') || user.roles.some(role => role.name === 'super-admin')) {
        return true;
      }

      return user.roles.some(role => {
        return role.permissions.some(permission => {
            return permission.name.startsWith('view')
        });
      });
    },
    accessibleResources,
    permissions,
    isLoading
  };

  return (
    <PolicyContext.Provider value={value}>
      {children}
    </PolicyContext.Provider>
  );
}

export function usePolicy() {
  const context = useContext(PolicyContext);
  if (context === undefined) {
    throw new Error('usePolicy must be used within a PolicyProvider');
  }
  return context;
}